import { useEffect, useState, type FormEvent } from 'react';
import dayjs from 'dayjs';
import type { BuildLocationsForDropdown, OrderAccountForDropdown, PlayerForSaveDto } from '@/api/models';
import type { OrderType } from '@/api/models/OrderSearch';
import Modal from '@/common/Modal/Modal';
import { CalendarDatePicker } from '@/common/DatePicker';
import EditableDropdown from '@/common/EditableDropdown';
import type { IDropdownOption } from '@/common/EditableDropdown/EditableDropdown';
import { BuildLocationsForDropdownDB, TournamentDB, OrderAccountsForDropdownDB, PlayerDB } from '@/db/repositories';
import { createNewOrderDraft, type NewOrderDraft } from './NewOrderDraft';
import { SingleSelectDropdown } from '@/common/SingleSelectDropdown/SingleSelectDropdown';
import { PlayerAutosuggest } from '@/common/PlayerAutosuggest/PlayerAutosuggest';
import styles from './NewOrderModal.module.scss';

interface Props {
  draft: NewOrderDraft;
  // eslint-disable-next-line no-unused-vars
  onChange: (draft: NewOrderDraft) => void;
  // eslint-disable-next-line no-unused-vars
  onNext: (draft: NewOrderDraft) => void;
  onClose: () => void;
}

const MAX_IN_HAND_DAYS_AHEAD = Number(import.meta.env.TOURPWA_ORDER_IN_HAND_DAYS_AHEAD) || 90;
const MIN_NEED_IN_HAND_DAYS = 8;

const orderTypes: { value: OrderType; label: string }[] = [
  { value: 'club', label: 'Club' },
  { value: 'putter', label: 'Putter' },
  { value: 'component', label: 'Component' },
];

export function NewOrderModal({ draft, onChange, onNext, onClose }: Props) {
  const [accounts, setAccounts] = useState<OrderAccountForDropdown[]>([]);
  const [locations, setLocations] = useState<BuildLocationsForDropdown[]>([]);
  const [tournamentOptions, setTournamentOptions] = useState<IDropdownOption[]>([]);
  const [players, setPlayers] = useState<PlayerForSaveDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const accountOptions = accounts.map((account) => ({ value: account.id.toString(), label: account.name }));
  const locationOptions = locations.map((location) => ({ value: location.id.toString(), label: location.name }));
  const needsPlayer = draft.orderType !== 'component';
  const tourRowClassName = draft.orderType === 'component' ? styles.locationRow : undefined;
  const accountInvalid = submitted && !draft.orderAccountId;
  const playerInvalid = submitted && needsPlayer && !draft.playerId;
  const buildLocationInvalid = submitted && !draft.buildLocationId;
  const shipFromInvalid = submitted && draft.orderType === 'component' && !draft.inventoryLocationId;
  const minInHandDate = dayjs().subtract(MIN_NEED_IN_HAND_DAYS, 'day').format('YYYY-MM-DD');
  const maxInHandDate = dayjs().add(MAX_IN_HAND_DAYS_AHEAD, 'day').format('YYYY-MM-DD');
  const dateInvalid = submitted && !draft.needInHandDate;
  const lookupsUnavailable = loading || loadError || accounts.length === 0 || locations.length === 0 || (needsPlayer && players.length === 0);

  const isValidInHandDate = () => {
    const value = draft.needInHandDate;
    if (!value) return false;

    const date = dayjs(value);
    if (!date.isValid()) return false;
    if (date.format('YYYY-MM-DD') !== value) return false;

    return value >= minInHandDate && value <= maxInHandDate;
  };

  const changeType = (orderType: OrderType) => {
    if (orderType === draft.orderType) return;
    setSubmitted(false);
    onChange(createNewOrderDraft(orderType));
  };

  const handleNext = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    const hasRequiredFields = Boolean(draft.orderAccountId && draft.buildLocationId && isValidInHandDate());
    const hasOrderTypeFields = draft.orderType === 'component' ? Boolean(draft.inventoryLocationId) : Boolean(draft.playerId);

    if (lookupsUnavailable) return;
    if (!hasRequiredFields || !hasOrderTypeFields) return;
    onNext({ ...draft });
  };

  useEffect(() => {
    const loadLookupData = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [accounts, players, locations, tournaments] = await Promise.all([OrderAccountsForDropdownDB.list(), PlayerDB.list(), BuildLocationsForDropdownDB.list(), TournamentDB.list()]);
        setAccounts(accounts.filter((account) => account.isActive).sort((a, b) => a.sortId - b.sortId));
        setPlayers(players);
        setLocations(locations.filter((location) => location.isActive).sort((a, b) => a.sortId - b.sortId));
        setTournamentOptions(
          tournaments
            .filter((tournament) => tournament.isActive)
            .sort((a, b) => a.sortId - b.sortId || a.name.localeCompare(b.name))
            .map((tournament) => ({ id: tournament.tournamentId.toString(), label: tournament.name })),
        );
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    void loadLookupData();
  }, [loadAttempt]);

  return (
    <Modal isOpen onClose={onClose} className={styles.modal} disableBackdropClose>
      <div className={styles.content} role="dialog">
        <h2 id="new-order-title">New Order</h2>
        {loading && <p role="status">Loading order options…</p>}
        {loadError && (
          <p role="alert">
            Order options could not be loaded.{' '}
            <button type="button" onClick={() => setLoadAttempt(loadAttempt + 1)}>
              Retry
            </button>
          </p>
        )}
        <form key={draft.orderType} onSubmit={handleNext} noValidate>
          <fieldset className={styles.types}>
            {orderTypes.map((type) => (
              <label key={type.value}>
                <input type="radio" name="orderType" value={type.value} checked={draft.orderType === type.value} onChange={() => changeType(type.value)} />
                {type.label}
              </label>
            ))}
          </fieldset>
          {draft.orderType === 'component' && (
            <>
              <SingleSelectDropdown
                label="Ship From"
                value={draft.inventoryLocationId?.toString()}
                options={locationOptions}
                disabled={loading || loadError}
                onChange={(value) => onChange({ ...draft, inventoryLocationId: Number(value) || null, inventoryLocationName: locations.find((location) => location.id === Number(value))?.name })}
              />
              {shipFromInvalid && (
                <p className={styles.error} role="alert">
                  Select a Ship From location.
                </p>
              )}
              <SingleSelectDropdown
                label="Ship To"
                value={draft.buildLocationId?.toString()}
                options={locationOptions}
                disabled={loading || loadError}
                onChange={(value) => onChange({ ...draft, buildLocationId: Number(value) || null, buildLocationName: locations.find((location) => location.id === Number(value))?.name })}
              />
              {buildLocationInvalid && (
                <p className={styles.error} role="alert">
                  Select a Ship To location.
                </p>
              )}
            </>
          )}
          <div className={tourRowClassName}>
            <SingleSelectDropdown
              label="Tour"
              value={draft.orderAccountId?.toString()}
              options={accountOptions}
              disabled={loading || loadError}
              onChange={(value) => {
                const orderAccountId = Number(value) || undefined;
                const account = accounts.find((account) => account.id === orderAccountId);
                onChange({ ...draft, orderAccountId: account?.id, orderAccountLabel: account?.name });
              }}
            />
            {draft.orderType === 'component' && (
              <fieldset className={`${styles.field} ${styles.dateField}`}>
                <legend>In-Hand Date</legend>
                <CalendarDatePicker
                  className={styles.datePicker}
                  calendarClassName={styles.calendarPopup}
                  min={minInHandDate}
                  max={maxInHandDate}
                  label="In-Hand Date"
                  value={draft.needInHandDate}
                  disabled={loading || loadError}
                  onSelect={(needInHandDate) => onChange({ ...draft, needInHandDate })}
                />
              </fieldset>
            )}
            {accountInvalid && (
              <p id="new-order-tour-error" className={styles.error} role="alert">
                Select a Tour.
              </p>
            )}
            {draft.orderType === 'component' && dateInvalid && (
              <p className={`${styles.error} ${styles.dateError}`} role="alert">
                Select a Date.
              </p>
            )}
          </div>
          {!loading && !loadError && accounts.length === 0 && <p role="status">No tours available. Sync lookup data and reopen this modal.</p>}
          {needsPlayer && (
            <>
              <PlayerAutosuggest
                label="Player"
                variant="field"
                players={players}
                disabled={loading || loadError}
                defaultPlayer={draft.playerId ? { playerId: draft.playerId, fullName: draft.playerName ?? '' } : undefined}
                onSelect={(player) => {
                  onChange({ ...draft, playerId: player?.playerId, playerName: player?.fullName });
                }}
              />
              {!loading && !loadError && players.length === 0 && <p role="status">No players available. Sync player data and reopen this modal.</p>}
              {playerInvalid && (
                <p className={styles.error} role="alert">
                  Select a Player from the search results.
                </p>
              )}
            </>
          )}
          {draft.orderType !== 'component' && (
            <div className={styles.locationRow}>
              <SingleSelectDropdown
                label="Build Location"
                value={draft.buildLocationId?.toString()}
                options={locationOptions}
                disabled={loading || loadError}
                onChange={(value) => onChange({ ...draft, buildLocationId: Number(value) || undefined, buildLocationName: locations.find((location) => location.id === Number(value))?.name })}
              />
              <fieldset className={`${styles.field} ${styles.dateField}`}>
                <legend>In-Hand Date</legend>
                <CalendarDatePicker
                  className={styles.datePicker}
                  calendarClassName={styles.calendarPopup}
                  min={minInHandDate}
                  max={maxInHandDate}
                  label="In-Hand Date"
                  value={draft.needInHandDate}
                  disabled={loading || loadError}
                  onSelect={(needInHandDate) => onChange({ ...draft, needInHandDate })}
                />
              </fieldset>
              {buildLocationInvalid && (
                <p className={styles.error} role="alert">
                  Select a Build Location.
                </p>
              )}
              {dateInvalid && (
                <p className={`${styles.error} ${styles.dateError}`} role="alert">
                  Select a Date.
                </p>
              )}
            </div>
          )}
          {!loading && !loadError && locations.length === 0 && <p role="status">No locations available. Sync lookup data and reopen this modal.</p>}
          <fieldset className={`${styles.field} ${styles.tournamentField}`} disabled={loading || loadError}>
            <legend>Tournament</legend>
            <div className={styles.tournamentControl}>
              <EditableDropdown
                label="Tournament"
                placeholder=""
                type="rectangle"
                alignment="left"
                initialId={draft.tournamentId?.toString() ?? ''}
                options={tournamentOptions}
                onChange={(id) => onChange({ ...draft, tournamentId: Number(id) || undefined })}
                className={styles.tournamentInput}
              />
              {!draft.tournamentId && <span className={styles.optional}>Optional</span>}
            </div>
          </fieldset>
          {draft.orderType === 'club' && (
            <label className={styles.van}>
              <input
                type="checkbox"
                checked={draft.isVanOrder}
                onChange={(event) => {
                  onChange({ ...draft, isVanOrder: event.target.checked });
                }}
              />
              Van Order
            </label>
          )}
          <div className={styles.buttons}>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.next} disabled={lookupsUnavailable}>
              Next
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
